import { useState, FormEvent, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Mail, Lock, User, AlertCircle, Zap, ChevronLeft } from 'lucide-react'
import { supabase } from '../services/supabase'
import { api } from '../services/api'
import { useAuthStore } from '../store/authStore'
import type { Player, FactionType } from '@territory-nexus/shared'
import { FACTION_CONFIG, RESOURCE_CONFIG } from '@territory-nexus/shared'

const FACTION_ICONS: Record<FactionType, string> = {
  solar: RESOURCE_CONFIG.energy.symbol,
  nano: RESOURCE_CONFIG.nanomaterials.symbol,
  quantum: RESOURCE_CONFIG.quantum.symbol,
  darkstar: RESOURCE_CONFIG.darkMatter.symbol,
}

const factionList = (Object.keys(FACTION_CONFIG) as FactionType[]).map(id => ({
  id,
  ...FACTION_CONFIG[id],
  icon: FACTION_ICONS[id],
}))

export default function Register() {
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [selectedFaction, setSelectedFaction] = useState<Player['faction'] | ''>('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [existingSession, setExistingSession] = useState(false)

  const navigate = useNavigate()
  const { setAuth } = useAuthStore()

  // Check if user already has a session (completing profile after login)
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user?.email) {
        setExistingSession(true)
        setEmail(session.user.email)
      }
    }
    checkSession()
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')

    if (!existingSession && password.length < 6) {
      setError('Security protocol requires minimum 6 character passcode')
      return
    }

    if (!selectedFaction) {
      setError('Commander faction alignment required')
      return
    }

    if (username.length < 3) {
      setError('Commander designation must be at least 3 characters')
      return
    }

    if (!acceptedTerms) {
      setError('You must confirm your age and accept the Terms of Service')
      return
    }

    setLoading(true)

    try {
      // Only sign up if no existing session
      if (!existingSession) {
        const { data: authData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
        })

        if (signUpError) {
          setError(signUpError.message)
          setLoading(false)
          return
        }

        if (!authData.user) {
          setError('Neural link initialization failed')
          setLoading(false)
          return
        }
      }

      // Create player profile
      const { data: { session } } = await supabase.auth.getSession()
      const response = await api.post<{ success: boolean; data: Player }>('/auth/register', {
        username,
        email,
        faction: selectedFaction,
      })

      if (response.data.success && session) {
        setAuth(session.access_token, response.data.data)
        navigate('/')
      }
    } catch (apiError: unknown) {
      const err = apiError as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error || 'Commander profile initialization failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[100dvh] relative flex items-start md:items-center justify-center bg-[#0a0a12] py-4 px-3 md:py-8 md:px-4 overflow-y-auto">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[128px] -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[128px] translate-x-1/3 translate-y-1/3" />
        <div className="absolute top-1/2 left-1/2 w-[400px] h-[400px] bg-blue-500/5 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2" />
        
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)`,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      {/* Main Card */}
      <div className="relative z-10 w-full max-w-4xl">
        <div className="relative backdrop-blur-xl bg-slate-900/80 border border-cyan-500/20 rounded-2xl shadow-2xl shadow-cyan-500/10 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-cyan-400 to-transparent" />
          
          <div className="p-4 md:p-10 space-y-5 md:space-y-8">
            {/* Header */}
            <div className="text-center space-y-2 md:space-y-4">
              <div className="flex items-center justify-center gap-2 md:gap-3">
                <div className="relative">
                  <div className="absolute inset-0 blur-xl bg-cyan-400/60 animate-pulse" />
                  <Zap className="relative w-7 h-7 md:w-10 md:h-10 text-cyan-400" />
                </div>
                <h1 className="text-xl md:text-3xl font-bold font-display tracking-wider">
                  <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                    TERRITORY NEXUS
                  </span>
                </h1>
              </div>
              <div>
                <h2 className="text-lg md:text-2xl font-bold text-white tracking-wide">
                  INITIALIZE COMMANDER PROFILE
                </h2>
                <p className="text-slate-400 text-xs md:text-sm mt-1 md:mt-2 font-mono">
                  Neural link establishment required // Choose your allegiance
                </p>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-red-500/50 bg-red-500/10">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                <p className="text-red-400 text-sm font-mono">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              {/* Commander ID - Always shown */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-300 tracking-wide">
                  Commander ID
                </label>
                <div className="relative group">
                  <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cyan-400 transition-colors">
                    <User className="w-5 h-5" />
                  </div>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter callsign"
                    required
                    className="w-full pl-11 pr-4 py-2.5 md:py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                  />
                </div>
              </div>

              {/* Email/Password - Only shown for new users */}
              {!existingSession && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-300 tracking-wide">
                        Email Uplink
                      </label>
                      <div className="relative group">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cyan-400 transition-colors">
                          <Mail className="w-5 h-5" />
                        </div>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="commander@nexus.com"
                          required
                          className="w-full pl-11 pr-4 py-2.5 md:py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-slate-300 tracking-wide">
                        Security Passcode
                      </label>
                      <div className="relative group">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-cyan-400 transition-colors">
                          <Lock className="w-5 h-5" />
                        </div>
                        <input
                          type="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Minimum 6 characters"
                          required
                          className="w-full pl-11 pr-4 py-2.5 md:py-3 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-100 text-sm placeholder-slate-500 focus:outline-none focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20 transition-all"
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Show email if completing profile */}
              {existingSession && (
                <div className="p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <p className="text-sm text-cyan-400">
                    <span className="font-mono">{'//'} </span>
                    Completing profile for: <span className="font-semibold">{email}</span>
                  </p>
                </div>
              )}

              {/* Faction Selection */}
              <div className="space-y-2 md:space-y-4">
                <label className="block text-sm font-medium text-slate-300 tracking-wide uppercase">
                  Select Faction Alignment
                </label>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-2 md:gap-4">
                  {factionList.map((faction) => {
                    const isSelected = selectedFaction === faction.id
                    return (
                      <button
                        key={faction.id}
                        type="button"
                        onClick={() => setSelectedFaction(faction.id)}
                        className={`relative p-2.5 md:p-4 rounded-xl border-2 transition-all duration-300 text-left group ${
                          isSelected
                            ? `${faction.border} ${faction.bg} shadow-lg ${faction.glow}`
                            : 'border-slate-700/50 bg-slate-800/30 hover:border-slate-600/50 hover:bg-slate-800/50'
                        }`}
                      >
                        <div className="flex items-start gap-2 md:gap-3">
                          <div className={`p-1.5 md:p-2 rounded-lg ${isSelected ? faction.bg : 'bg-slate-700/50'} transition-colors`}>
                            <span className={`text-lg md:text-2xl ${isSelected ? `text-${faction.color}-400` : 'text-slate-400'}`}>
                              {faction.icon}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className={`text-sm md:text-base font-semibold ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                              {faction.name}
                            </h3>
                            <p className="text-xs md:text-sm text-slate-400 mt-0.5 md:mt-1 line-clamp-2">
                              {faction.description}
                            </p>
                          </div>
                          {isSelected && (
                            <div className={`w-3 h-3 rounded-full bg-gradient-to-r ${faction.gradient}`} />
                          )}
                        </div>

                        {/* Selection indicator line */}
                        {isSelected && (
                          <div className={`absolute bottom-0 left-4 right-4 h-[2px] bg-gradient-to-r ${faction.gradient}`} />
                        )}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Age & Terms Confirmation */}
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-5 h-5 border-2 border-slate-600 rounded bg-slate-800/50 peer-checked:bg-cyan-500/20 peer-checked:border-cyan-500 transition-all flex items-center justify-center">
                    {acceptedTerms && (
                      <svg className="w-3 h-3 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-xs text-slate-400 leading-relaxed">
                  I confirm I am at least 13 years old and agree to the{' '}
                  <Link to="/terms" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2" onClick={(e) => e.stopPropagation()}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2" onClick={(e) => e.stopPropagation()}>
                    Privacy Policy
                  </Link>.
                </span>
              </label>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="relative w-full py-3 md:py-4 rounded-lg font-display font-semibold text-sm md:text-base tracking-wider text-white overflow-hidden group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-blue-500 to-purple-500" />
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="absolute inset-0 blur-xl bg-cyan-500/30" />
                </div>
                <span className="relative flex items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>INITIALIZING NEURAL LINK...</span>
                    </>
                  ) : (
                    <span>ACTIVATE COMMANDER PROFILE</span>
                  )}
                </span>
              </button>

              {/* Back to login */}
              <div className="text-center pt-3 md:pt-4 border-t border-slate-700/50">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors text-sm"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Return to login portal</span>
                </Link>
              </div>
            </form>
          </div>

          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-purple-400/50 to-transparent" />
        </div>
      </div>
    </div>
  )
}
